import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import * as XLSX from 'xlsx';
import { sessionAPI } from '../services/api';
import { getPublicApiOrigin } from '../config/env';
import { getSessionRecommendation } from '../utils/hiringRecommendation';
import { USE_DEMO_DATA } from '../data/demoMode';
import { DUMMY_SESSIONS, withCurrentRecruiterInDemoSessions } from '../data/dashboardDummyData';
import './AdminDashboard.css';
import AddCandidatesLauncher from './position/AddCandidatesLauncher';

const fmtDate = (value) => {
  if (!value) return '—';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
};

const T = {
  bg: '#F8FAFC',
  brand: '#6366F1',
  brand2: '#8B5CF6',
  t0: '#0F172A',
  t1: '#1E293B',
  t2: '#475569',
  t3: '#94A3B8',
  bdr: '#E2E8F0',
  green: '#10B981',
  amber: '#F59E0B',
  blue: '#3B82F6',
  purple: '#6366F1',
};

function Tag({ label, color }) {
  return (
    <span
      style={{
        fontSize: 10,
        padding: '3px 9px',
        borderRadius: 20,
        background: color + '18',
        color,
        fontWeight: 700,
      }}
    >
      {label}
    </span>
  );
}

const Candidates = () => {
  const navigate = useNavigate();
  const [showAdd, setShowAdd] = useState(false);
  const [searchParams] = useSearchParams();
  const recFromUrl = searchParams.get('rec');
  const flaggedFromUrl = searchParams.get('flagged') === '1';
  const initialRec =
    recFromUrl && ['all', 'strong_yes', 'yes', 'no'].includes(recFromUrl) ? recFromUrl : 'all';
  const [candidates, setCandidates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [recFilter, setRecFilter] = useState(initialRec);
  const [flaggedOnly, setFlaggedOnly] = useState(flaggedFromUrl);
  const [page, setPage] = useState(1);
  const pageSize = 10;
  const [inviteSent, setInviteSent] = useState({});

  // Export (Excel) state
  const [exporting, setExporting] = useState(false);

  const handleSendInvite = async (candidateId) => {
    if (!candidateId) return;
    try {
      const token = localStorage.getItem('token');
      const base = getPublicApiOrigin();
      const res = await fetch(`${base}/api/candidates/${candidateId}/send-invite`, {
        method: 'POST',
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      const data = await res.json().catch(() => ({}));
      if (res.ok && data.success) {
        setInviteSent((prev) => ({ ...prev, [candidateId]: true }));
      } else {
        const detail = typeof data.detail === 'string' ? data.detail : (Array.isArray(data.detail) ? data.detail.map((d) => d.msg || d).join(' ') : null);
        alert(detail || data.message || 'Failed to send invite.');
      }
    } catch (err) {
      alert('Failed to send invite. Please try again.');
    }
  };

  const loadCandidates = async () => {
    try {
      setLoading(true);
      if (USE_DEMO_DATA) {
        setCandidates(withCurrentRecruiterInDemoSessions(DUMMY_SESSIONS));
        return;
      }
      const response = await sessionAPI.getAllSessions();
      setCandidates(response.data || []);
    } catch (error) {
      console.error('Error loading candidates:', error);
      setCandidates([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadCandidates();

    // Refetch when user comes back to this tab
    window.addEventListener('focus', loadCandidates);
    return () => {
      window.removeEventListener('focus', loadCandidates);
    };
  }, []);

  const filtered = (candidates ?? []).filter(c => {
    const s = search.toLowerCase();
    const matchSearch = !s || (
      (c.name ?? c.candidate_name ?? "").toLowerCase().includes(s) ||
      (c.email ?? c.candidate_email ?? "").toLowerCase().includes(s) ||
      (c.role ?? c.position ?? c.job_role ?? "").toLowerCase().includes(s)
    );
    const { key: rec } = getSessionRecommendation(c);
    const matchRec = recFilter === "all" || rec === recFilter;
    const viol = Number(c.total_violations ?? c.violations ?? 0);
    const matchFlagged = !flaggedOnly || viol > 10;
    return matchSearch && matchRec && matchFlagged;
  });
  const totalPages = Math.max(1, Math.ceil(filtered.length / pageSize));
  const startIndex = (page - 1) * pageSize;
  const paginated = filtered.slice(startIndex, startIndex + pageSize);

  useEffect(() => {
    setPage(1);
  }, [search, recFilter, flaggedOnly]);

  useEffect(() => {
    if (page > totalPages) {
      setPage(totalPages);
    }
  }, [page, totalPages]);

  const inviteStatusLabel = (c) => {
    const status = (c.status ?? 'pending').toLowerCase();
    if (status === 'completed') return 'Interview Done';
    if (status === 'in_progress') return 'In Progress';
    return 'Invited';
  };

  const handleExport = () => {
    const rows = (filtered ?? []).map((c) => {
      const score = c.overall_score;
      return {
        Name: c.name ?? c.candidate_name ?? '',
        Email: c.email ?? c.candidate_email ?? '',
        Position: c.campaign_name ?? c.campaign ?? '',
        Role: c.role ?? c.position ?? c.job_role ?? '',
        Recruiter: c.recruiter_name ?? '',
        'Invited Date': fmtDate(c.invite_sent_at ?? c.created_at),
        Status: inviteStatusLabel(c),
        'Completed Date': fmtDate(c.completed_at),
        'Overall Score': score != null && score !== '' ? Number(score).toFixed(1) : '',
        Recommendation: getSessionRecommendation(c).label,
      };
    });
    if (rows.length === 0) {
      alert('There are no candidates to export.');
      return;
    }
    setExporting(true);
    try {
      const ws = XLSX.utils.json_to_sheet(rows);
      ws['!cols'] = [
        { wch: 22 }, { wch: 30 }, { wch: 28 }, { wch: 22 }, { wch: 20 }, { wch: 14 },
        { wch: 15 }, { wch: 15 }, { wch: 13 }, { wch: 16 },
      ];
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, 'Candidates');
      const stamp = new Date().toISOString().slice(0, 10);
      XLSX.writeFile(wb, `wizetok-candidates-${stamp}.xlsx`);
    } finally {
      setExporting(false);
    }
  };

  return (
    <div style={{ flex: 1, overflowY: "auto", minHeight: 0, padding: "28px 32px" }}>
      {showAdd && <AddCandidatesLauncher onClose={() => setShowAdd(false)} />}

      {/* Page header */}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 20 }}>
        <div>
          <div style={{ fontSize: 22, fontWeight: 700, color: "#0F172A", marginBottom: 3 }}>Candidates</div>
          <div style={{ fontSize: 12, color: "#475569" }}>
            {(candidates ?? []).length} total candidates across all positions.
          </div>
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <button
            type="button"
            onClick={handleExport}
            disabled={exporting}
            style={{ display: "inline-flex", alignItems: "center", gap: 7, fontSize: 12, background: "#6366F1", color: "white", border: "none", padding: "8px 16px", borderRadius: 8, cursor: exporting ? "wait" : "pointer", fontWeight: 600, fontFamily: "inherit", boxShadow: "0 2px 8px rgba(99,102,241,.3)", transition: "background .15s" }}
            onMouseEnter={e => { if (!exporting) e.currentTarget.style.background = "#4F46E5"; }}
            onMouseLeave={e => { if (!exporting) e.currentTarget.style.background = "#6366F1"; }}
          >
            <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
              <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
              <polyline points="7 10 12 15 17 10" />
              <line x1="12" y1="15" x2="12" y2="3" />
            </svg>
            {exporting ? "Exporting…" : "Export"}
          </button>
          <button
            type="button"
            onClick={() => setShowAdd(true)}
            style={{ fontSize: 12, background: "#6366F1", color: "white", border: "none", padding: "8px 18px", borderRadius: 8, cursor: "pointer", fontWeight: 600, fontFamily: "inherit", boxShadow: "0 2px 8px rgba(99,102,241,.3)", transition: "background .15s" }}
            onMouseEnter={e => e.currentTarget.style.background = "#4F46E5"}
            onMouseLeave={e => e.currentTarget.style.background = "#6366F1"}
          >
            + Add Candidate
          </button>
        </div>
      </div>

      {/* Search + filter row */}
      <div style={{ display: "flex", gap: 10, marginBottom: 20, alignItems: "center" }}>
        <div style={{ position: "relative", flex: 1, maxWidth: 360 }}>
          <span
            style={{
              position: "absolute",
              left: 8,
              top: "50%",
              transform: "translateY(-50%)",
              width: 30,
              height: 30,
              borderRadius: 8,
              background: "rgba(99,102,241,.1)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              pointerEvents: "none",
            }}
            aria-hidden
          >
            <svg
              viewBox="0 0 24 24"
              width="15"
              height="15"
              fill="none"
              stroke="#6366F1"
              strokeWidth="1.75"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <circle cx="11" cy="11" r="8" />
              <path d="m21 21-4.35-4.35" />
            </svg>
          </span>
          <input
            value={search}
            onChange={e => setSearch(e.target.value)}
            placeholder="Search by name, email or role..."
            style={{ width: "100%", border: "1.5px solid #E2E8F0", borderRadius: 9, padding: "8px 12px 8px 48px", fontSize: 12, color: "#1E293B", outline: "none", background: "white", transition: "border-color .15s", fontFamily: "inherit" }}
            onFocus={e => e.target.style.borderColor = "#6366F1"}
            onBlur={e => e.target.style.borderColor = "#E2E8F0"}
          />
        </div>
        <div
          style={{
            display: "flex",
            gap: 2,
            marginBottom: 0,
            background: "#F3F4F6",
            padding: 3,
            borderRadius: 8,
            border: "1px solid #E5E7EB",
            width: "fit-content",
          }}
        >
          {[
            { value: "all", label: "All" },
            { value: "strong_yes", label: "Shortlisted" },
            { value: "yes", label: "Under Review" },
            { value: "no", label: "Pending" },
          ].map(f => (
            <button
              key={f.value}
              onClick={() => {
                setFlaggedOnly(false);
                setRecFilter(f.value);
              }}
              style={
                recFilter === f.value && !flaggedOnly
                  ? {
                      padding: "5px 14px",
                      borderRadius: 6,
                      border: "none",
                      background: "#FFFFFF",
                      color: "#111827",
                      fontSize: 12,
                      fontWeight: 600,
                      cursor: "pointer",
                      fontFamily: "inherit",
                      transition: "all .14s",
                      boxShadow: "0 1px 2px rgba(0,0,0,.08)",
                      whiteSpace: "nowrap",
                    }
                  : {
                      padding: "5px 14px",
                      borderRadius: 6,
                      border: "none",
                      background: "transparent",
                      color: "#6B7280",
                      fontSize: 12,
                      fontWeight: 400,
                      cursor: "pointer",
                      fontFamily: "inherit",
                      transition: "all .14s",
                      whiteSpace: "nowrap",
                    }
              }
            >
              {f.label}
            </button>
          ))}
          <button
            type="button"
            onClick={() => {
              setRecFilter("all");
              setFlaggedOnly(true);
            }}
            style={
              flaggedOnly
                ? {
                    padding: "5px 14px",
                    borderRadius: 6,
                    border: "none",
                    background: "#FFFFFF",
                    color: "#DC2626",
                    fontSize: 12,
                    fontWeight: 600,
                    cursor: "pointer",
                    fontFamily: "inherit",
                    transition: "all .14s",
                    boxShadow: "0 1px 2px rgba(0,0,0,.08)",
                    whiteSpace: "nowrap",
                  }
                : {
                    padding: "5px 14px",
                    borderRadius: 6,
                    border: "none",
                    background: "transparent",
                    color: "#6B7280",
                    fontSize: 12,
                    fontWeight: 400,
                    cursor: "pointer",
                    fontFamily: "inherit",
                    transition: "all .14s",
                    whiteSpace: "nowrap",
                  }
            }
          >
            Flagged
          </button>
        </div>
      </div>

      {/* Table */}
      <div style={{ background: "white", border: "1px solid #E2E8F0", borderRadius: 14, overflow: "hidden", boxShadow: "0 1px 4px rgba(0,0,0,.05)" }}>
        <table style={{ width: "100%", borderCollapse: "collapse" }}>
          <thead>
            <tr>
              {["Name", "Position", "Invited Date", "Invite Status", "Action"].map(h => (
                <th key={h} style={{ padding: "9px 18px", fontSize: 9, color: "#94A3B8", textTransform: "uppercase", letterSpacing: "1.1px", fontWeight: 700, borderBottom: "1px solid #E2E8F0", background: "#F8FAFC", textAlign: "left" }}>
                  {h}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {paginated.map((c, i) => {
              const name = c.name ?? c.candidate_name ?? 'Unknown';
              const email = c.email ?? c.candidate_email ?? '';
              const campaign = c.campaign_name ?? c.campaign ?? '';
              const role = c.role ?? c.position ?? c.job_role ?? '';
              const invitedDate = fmtDate(c.invite_sent_at ?? c.created_at);
              const status = c.status ?? 'pending';
              const initial = (name && name[0] ? name[0] : '?').toUpperCase();

              return (
                <tr
                  key={c.id ?? `${startIndex + i}`}
                  style={{ transition: 'background .14s', cursor: 'pointer' }}
                  onMouseEnter={e => { e.currentTarget.style.background = T.bg; }}
                  onMouseLeave={e => { e.currentTarget.style.background = 'none'; }}
                >
                  <td style={{ padding: '13px 18px', borderBottom: '1px solid ' + T.bdr }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                      <div
                        style={{
                          width: 32,
                          height: 32,
                          borderRadius: '50%',
                          background: `linear-gradient(135deg,${T.brand}30,${T.brand2}20)`,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          fontSize: 12,
                          fontWeight: 700,
                          color: T.brand,
                          flexShrink: 0,
                        }}
                      >
                        {initial}
                      </div>
                      <div>
                        <div style={{ fontSize: 13, fontWeight: 600, color: T.t0 }}>{name}</div>
                        <div style={{ fontSize: 10, color: T.t3 }}>{email}</div>
                      </div>
                    </div>
                  </td>

                  <td style={{ padding: '13px 18px', borderBottom: '1px solid ' + T.bdr }}>
                    <div style={{ fontSize: 12, color: T.t1 }}>{campaign}</div>
                    <div style={{ fontSize: 10, color: T.t3 }}>{role}</div>
                  </td>

                  <td style={{ padding: '13px 18px', fontSize: 12, color: T.t2, borderBottom: '1px solid ' + T.bdr }}>
                    {invitedDate}
                  </td>

                  <td style={{ padding: '13px 18px', borderBottom: '1px solid ' + T.bdr }}>
                    <Tag
                      label={status === 'completed' ? 'Interview Done' : status === 'in_progress' ? 'In Progress' : 'Invited'}
                      color={status === 'completed' ? T.green : status === 'in_progress' ? T.amber : T.blue}
                    />
                  </td>

                  <td style={{ padding: '13px 18px', borderBottom: '1px solid ' + T.bdr }}>
                    {(() => {
                      const inviteStatus = (c.invite_status ?? '').toLowerCase();
                      const sessionStatus = (c.status ?? '').toLowerCase();
                      const showViewReport =
                        sessionStatus === 'completed' ||
                        inviteStatus === 'completed' ||
                        c.overall_score !== null;

                      if (showViewReport) {
                        return (
                          <button
                            type="button"
                            onClick={() => navigate(`/admin/reports?sessionId=${c.id ?? c.session_id}`)}
                            style={{
                              fontSize: 11,
                              color: T.brand,
                              background: 'none',
                              border: 'none',
                              cursor: 'pointer',
                              fontWeight: 600,
                              fontFamily: 'inherit',
                            }}
                          >
                            View Report →
                          </button>
                        );
                      }
                      if (inviteStatus === 'started' || sessionStatus === 'in_progress') {
                        return (
                          <span style={{ fontSize: 11, color: T.t3, fontWeight: 600 }}>In Progress</span>
                        );
                      }
                      if (!c.candidate_id) {
                        return (
                          <span style={{ fontSize: 11, color: T.t3 }} title="No candidate record linked — cannot email invite">
                            —
                          </span>
                        );
                      }
                      if (inviteStatus === 'invited') {
                        return (
                          <button
                            type="button"
                            onClick={() => handleSendInvite(c.candidate_id)}
                            style={{
                              fontSize: 11,
                              color: T.brand,
                              background: 'none',
                              border: `1px solid ${T.brand}`,
                              padding: '4px 10px',
                              borderRadius: 6,
                              cursor: 'pointer',
                              fontWeight: 600,
                              fontFamily: 'inherit',
                            }}
                          >
                            Resend Invite
                          </button>
                        );
                      }
                      return (
                        <button
                          type="button"
                          onClick={() => handleSendInvite(c.candidate_id)}
                          disabled={inviteSent[c.candidate_id]}
                          style={{
                            fontSize: 11,
                            color: inviteSent[c.candidate_id] ? T.green : T.brand,
                            background: 'none',
                            border: `1px solid ${inviteSent[c.candidate_id] ? T.green : T.brand}`,
                            padding: '4px 10px',
                            borderRadius: 6,
                            cursor: inviteSent[c.candidate_id] ? 'default' : 'pointer',
                            fontWeight: 600,
                            fontFamily: 'inherit',
                            opacity: inviteSent[c.candidate_id] ? 0.7 : 1,
                          }}
                        >
                          {inviteSent[c.candidate_id] ? '✓ Sent' : 'Send Invite'}
                        </button>
                      );
                    })()}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>

        {loading && (
          <div style={{ padding: "24px", textAlign: "center", color: "#94A3B8", fontSize: 13 }}>
            Loading candidates...
          </div>
        )}

        {!loading && filtered.length === 0 && (
          <div style={{ padding: "48px", textAlign: "center", color: "#94A3B8", fontSize: 13 }}>
            {search ? "No candidates match your search." : "No candidates yet."}
          </div>
        )}
        {!loading && filtered.length > 0 && (
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "12px 18px", borderTop: "1px solid #E2E8F0", background: "#F8FAFC" }}>
            <div style={{ fontSize: 11, color: "#64748B" }}>
              Showing {startIndex + 1}-{Math.min(startIndex + pageSize, filtered.length)} of {filtered.length}
            </div>
            <div style={{ display: "flex", gap: 6, alignItems: "center" }}>
              <button
                onClick={() => setPage(p => Math.max(1, p - 1))}
                disabled={page === 1}
                style={{
                  fontSize: 11,
                  padding: "5px 10px",
                  borderRadius: 7,
                  border: "1px solid #E2E8F0",
                  background: page === 1 ? "#F1F5F9" : "white",
                  color: page === 1 ? "#94A3B8" : "#475569",
                  cursor: page === 1 ? "not-allowed" : "pointer",
                  fontFamily: "inherit",
                }}
              >
                Prev
              </button>
              {Array.from({ length: totalPages }, (_, idx) => idx + 1).slice(
                Math.max(0, page - 3),
                Math.min(totalPages, page + 2)
              ).map(pn => (
                <button
                  key={pn}
                  onClick={() => setPage(pn)}
                  style={{
                    fontSize: 11,
                    minWidth: 28,
                    height: 28,
                    borderRadius: 7,
                    border: "1px solid " + (pn === page ? "#6366F1" : "#E2E8F0"),
                    background: pn === page ? "rgba(99,102,241,.1)" : "white",
                    color: pn === page ? "#6366F1" : "#475569",
                    cursor: "pointer",
                    fontFamily: "inherit",
                    fontWeight: pn === page ? 700 : 500,
                  }}
                >
                  {pn}
                </button>
              ))}
              <button
                onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                disabled={page === totalPages}
                style={{
                  fontSize: 11,
                  padding: "5px 10px",
                  borderRadius: 7,
                  border: "1px solid #E2E8F0",
                  background: page === totalPages ? "#F1F5F9" : "white",
                  color: page === totalPages ? "#94A3B8" : "#475569",
                  cursor: page === totalPages ? "not-allowed" : "pointer",
                  fontFamily: "inherit",
                }}
              >
                Next
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default Candidates;
